/**
 * Meeting Links (Evergreen Rooms)
 * --------------------------------
 * A persistent meeting room that lives at a permanent /m/<public_id> URL.
 * The host can re-enter anytime, and anyone with the link can join
 * once the host is in the room (or directly, if guest access + no
 * waiting room).
 *
 * Backed by `meetings` with `is_persistent: true` — the existing
 * Lobby / Agora / MeetingRoom plumbing is reused; we only had to teach
 * the lobby + agora/join to skip the "ended/410" branch for persistent
 * rooms (server.py + Lobby.jsx).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Copy, Trash2, ExternalLink, Video, Pencil, MoreVertical, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { userBrowserTimezone } from "@/lib/time";
import { copyText } from "@/lib/clipboard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function MeetingLinks() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    if (!currentWorkspace) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/meetings`, {
        params: { workspace_id: currentWorkspace.workspace_id, persistent_only: true },
      });
      setRows(data);
    } catch {
      toast.error("Could not load Meeting Links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspace]);

  const linkFor = (m) =>
    // Prefer the custom slug URL when set — it's the URL the user
    // actually wants on their socials / docs.
    `${window.location.origin}/m/${m.custom_slug || m.public_id}`;

  const onCopy = (m) => {
    copyText(linkFor(m)).then(() => toast.success("Link copied"));
  };

  const onEnter = (m) => {
    navigate(`/m/${m.public_id}`);
  };

  const onDelete = async (m) => {
    if (!window.confirm(`Delete Meeting Link "${m.title}"? Anyone with the link will lose access.`)) return;
    try {
      await api.delete(`/meetings/${m.meeting_id}`);
      toast.success("Meeting link deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const onRotate = async (m) => {
    // "Rotate" = clone the meeting under a fresh public_id so the old URL
    // stops working. Done by creating a new persistent meeting with the
    // same title + then deleting the old one. Cheap and bulletproof.
    if (!window.confirm(`Rotate URL for "${m.title}"? The old link will stop working immediately.`)) return;
    try {
      await api.post(`/meetings`, {
        title: m.title,
        description: m.description || "",
        workspace_id: m.workspace_id,
        duration_minutes: m.duration_minutes || 60,
        timezone: m.timezone || user?.timezone || userBrowserTimezone(),
        guest_access_enabled: m.guest_access_enabled,
        waiting_room_enabled: m.waiting_room_enabled,
        is_persistent: true,
        notify_invitees: false,
      });
      await api.delete(`/meetings/${m.meeting_id}`);
      toast.success("New URL minted — old link disabled");
      load();
    } catch {
      toast.error("Couldn't rotate URL");
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="meeting-links-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#4a4a52] max-w-2xl">
          Evergreen meeting rooms. Each one has a permanent URL you can put on a
          calendar event, in a Slack channel, on a doc — anyone with the link
          can join once you're inside. Re-enter anytime.
        </p>
        <button
          onClick={() => setShowCreate(true)}
          className="bloq-btn-primary inline-flex items-center gap-2"
          data-testid="new-meeting-link-btn"
        >
          <Plus className="w-4 h-4" /> New meeting link
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#82828c]">Loading meeting links…</p>
      ) : rows.length === 0 ? (
        <div className="bloq-card p-10 text-center" data-testid="meeting-links-empty">
          <Video className="w-10 h-10 mx-auto text-[#82828c] mb-3" />
          <p className="bloq-headline text-xl mb-1 text-[#005e7a]">No meeting links yet</p>
          <p className="text-sm text-[#82828c] mb-5">
            Spin up a permanent room — perfect for office hours, recurring 1:1s,
            and "always-on" client rooms.
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="bloq-btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Create your first meeting link
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((m) => (
            <div
              key={m.meeting_id}
              className="bloq-card p-5 flex flex-col gap-3"
              data-testid={`meeting-link-card-${m.meeting_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest"
                  style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}
                >
                  Evergreen
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className="p-1 rounded hover:bg-[#f5efe6]"
                    data-testid={`meeting-link-menu-${m.meeting_id}`}
                  >
                    <MoreVertical className="w-4 h-4 text-[#82828c]" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditTarget(m)}>
                      <Pencil className="w-4 h-4 mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onRotate(m)}>
                      <RefreshCw className="w-4 h-4 mr-2" /> Rotate URL
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDelete(m)}
                      className="text-red-600 focus:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <button onClick={() => onEnter(m)} className="text-left">
                <p className="bloq-headline text-lg leading-tight text-[#005e7a]">{m.title}</p>
                {m.description && (
                  <p className="text-sm text-[#82828c] mt-1.5 line-clamp-2">{m.description}</p>
                )}
              </button>
              <div className="text-xs text-[#82828c] font-mono break-all bg-[#f5efe6] p-2">
                {linkFor(m)}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => onCopy(m)}
                  className="bloq-btn-secondary text-xs flex-1 inline-flex items-center justify-center gap-1.5"
                  data-testid={`copy-meeting-link-${m.meeting_id}`}
                >
                  <Copy className="w-3 h-3" /> Copy link
                </button>
                <button
                  onClick={() => onEnter(m)}
                  className="bloq-btn-primary text-xs flex-1 inline-flex items-center justify-center gap-1.5"
                  data-testid={`enter-meeting-link-${m.meeting_id}`}
                >
                  <ExternalLink className="w-3 h-3" /> Enter room
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <MeetingLinkDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
          workspace={currentWorkspace}
          user={user}
        />
      )}
      {editTarget && (
        <MeetingLinkDialog
          existing={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
          workspace={currentWorkspace}
          user={user}
        />
      )}
    </div>
  );
}

function MeetingLinkDialog({ existing, onClose, onSaved, workspace, user }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    title: existing?.title || "",
    description: existing?.description || "",
    guest_access_enabled: existing?.guest_access_enabled ?? true,
    waiting_room_enabled: existing?.waiting_room_enabled ?? false,
    custom_slug: existing?.custom_slug || "",
  });
  const [slugError, setSlugError] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Give your meeting link a title");
      return;
    }
    // Strip leading/trailing hyphens just before submit (allowed mid-typing
    // so users can build words like "office-hours" without the dash being
    // eaten between chars).
    const cleanSlug = (form.custom_slug || "").replace(/^-+|-+$/g, "");
    setBusy(true);
    try {
      if (isEdit) {
        // Only send custom_slug on edit when it actually changed — saves
        // round-trips through the uniqueness validator for unchanged
        // rooms.
        const patch = {
          title: form.title.trim(),
          description: form.description,
          guest_access_enabled: form.guest_access_enabled,
          waiting_room_enabled: form.waiting_room_enabled,
        };
        if (cleanSlug !== (existing.custom_slug || "")) {
          patch.custom_slug = cleanSlug || null;
        }
        await api.patch(`/meetings/${existing.meeting_id}`, patch);
        toast.success("Meeting link updated");
      } else {
        await api.post(`/meetings`, {
          title: form.title.trim(),
          description: form.description,
          workspace_id: workspace.workspace_id,
          duration_minutes: 60,
          timezone: user?.timezone || userBrowserTimezone(),
          guest_access_enabled: form.guest_access_enabled,
          waiting_room_enabled: form.waiting_room_enabled,
          is_persistent: true,
          custom_slug: cleanSlug || undefined,
          notify_invitees: false,
        });
        toast.success("Meeting link created");
      }
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not save";
      if (detail.toLowerCase().includes("slug") || detail.toLowerCase().includes("custom url")) {
        setSlugError(detail);
      }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="meeting-link-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit meeting link" : "New meeting link"}</DialogTitle>
          <DialogDescription>
            A permanent room URL you can share anywhere. Re-enter as often as
            you like.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Title *</label>
            <input
              className="bloq-input mt-1"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Weekly office hours"
              data-testid="meeting-link-title-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Description</label>
            <textarea
              rows={3}
              className="bloq-input mt-1"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What's this room for? (optional)"
              data-testid="meeting-link-desc-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
              Custom URL (optional)
            </label>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-[#82828c] font-mono">/m/</span>
              <input
                className="bloq-input text-sm flex-1 min-w-[140px]"
                value={form.custom_slug}
                onChange={(e) => {
                  // Live cleanup: lowercase + replace runs of unsafe
                  // chars with a single hyphen. Leading/trailing hyphens
                  // are preserved during typing (see save()).
                  const cleaned = (e.target.value || "")
                    .toLowerCase()
                    .replace(/[^a-z0-9-]+/g, "-");
                  setForm((f) => ({ ...f, custom_slug: cleaned }));
                  setSlugError("");
                }}
                onBlur={() => {
                  setForm((f) => ({ ...f, custom_slug: (f.custom_slug || "").replace(/^-+|-+$/g, "") }));
                }}
                placeholder="office-hours"
                maxLength={40}
                data-testid="meeting-link-slug-input"
              />
            </div>
            <p className="text-[11px] text-[#82828c] mt-1">
              Lowercase letters, numbers, and hyphens · 3–40 chars · must be unique.
              Leave blank to keep the auto-generated URL.
            </p>
            {slugError && (
              <p className="text-xs text-red-600 mt-1" data-testid="meeting-link-slug-error">{slugError}</p>
            )}
          </div>
          <label className="flex items-center justify-between gap-3 cursor-pointer py-1">
            <div>
              <p className="text-sm font-medium">Allow guests</p>
              <p className="text-xs text-[#82828c]">Anyone with the link can join without a Bloq account.</p>
            </div>
            <input
              type="checkbox"
              checked={form.guest_access_enabled}
              onChange={(e) => setForm({ ...form, guest_access_enabled: e.target.checked })}
              className="w-4 h-4 accent-[#005e7a]"
              data-testid="meeting-link-guests-checkbox"
            />
          </label>
          <label className="flex items-center justify-between gap-3 cursor-pointer py-1">
            <div>
              <p className="text-sm font-medium">Waiting room</p>
              <p className="text-xs text-[#82828c]">You approve each joiner before they enter.</p>
            </div>
            <input
              type="checkbox"
              checked={form.waiting_room_enabled}
              onChange={(e) => setForm({ ...form, waiting_room_enabled: e.target.checked })}
              className="w-4 h-4 accent-[#005e7a]"
              data-testid="meeting-link-wr-checkbox"
            />
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary" disabled={busy}>Cancel</button>
          <button
            onClick={save}
            disabled={busy}
            className="bloq-btn-primary"
            data-testid="meeting-link-save"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create link"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Used by the parent Meetings page so the "Meeting Links" tab can be a
// drop-in panel.
export { MeetingLinkDialog };
